import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API, useAuth } from "../App";
import { Wallet, ArrowRight, AlertCircle, CheckCircle2, IndianRupee, Clock, BadgePercent, Building, Calendar, Banknote, X } from "lucide-react";

export default function Earnings() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [payoutAcc, setPayoutAcc] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [editingAcc, setEditingAcc] = useState(false);

  const load = async () => {
    const [a, b, c] = await Promise.all([
      axios.get(`${API}/partner/earnings`).catch(() => ({ data: { summary: { pending: 0, in_payout: 0, paid: 0, lifetime_gross: 0, lifetime_fees: 0, rows_count: 0 }, rows: [] } })),
      axios.get(`${API}/partner/payout-account`).catch(() => ({ data: {} })),
      axios.get(`${API}/partner/payouts`).catch(() => ({ data: { items: [] } })),
    ]);
    setData(a.data);
    setPayoutAcc(b.data?.user_id ? b.data : null);
    setPayouts(c.data.items || []);
  };
  useEffect(() => { load(); }, []);

  if (!user) return null;
  if (!data) return <div className="text-ink-soft">Loading…</div>;

  const accReady = !!(payoutAcc && (payoutAcc.upi_id || (payoutAcc.bank_account_number && payoutAcc.bank_ifsc) || payoutAcc.stripe_connected_account_id));

  return (
    <div className="space-y-6" data-testid="earnings-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-brand font-bold inline-flex items-center gap-1"><Wallet className="w-4 h-4"/>Earnings & Payouts</div>
          <h1 className="font-heading text-3xl font-extrabold mt-1">Your money</h1>
          <p className="text-ink-soft text-sm">PetBond settles automatically every Monday. Add a payout account once — we'll handle the rest.</p>
        </div>
      </header>

      {!accReady && (
        <div className="rounded-3xl bg-gradient-to-br from-brand to-brand-hover text-white p-5 md:p-6 shadow-soft" data-testid="payout-cta">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-bold opacity-90"><AlertCircle className="w-4 h-4"/>Action needed</div>
          <h2 className="font-heading text-2xl font-extrabold mt-1">Add your payout details to receive money</h2>
          <p className="opacity-95 text-sm mt-1">Without this we can't settle your weekly earnings.</p>
          <button onClick={() => setEditingAcc(true)} data-testid="add-payout-btn" className="mt-4 bg-white text-brand rounded-full px-5 py-2.5 text-sm font-extrabold inline-flex items-center gap-2 hover:bg-surface-muted">
            Add payout details <ArrowRight className="w-4 h-4"/>
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Pending"   value={`₹${data.summary.pending}`}   icon={Clock}        color="bg-sun/40 text-ink" testid="tile-pending"/>
        <Tile label="In payout" value={`₹${data.summary.in_payout}`} icon={Calendar}     color="bg-sky/15 text-sky" testid="tile-in-payout"/>
        <Tile label="Paid out"  value={`₹${data.summary.paid}`}      icon={CheckCircle2} color="bg-leaf/40 text-ink" testid="tile-paid"/>
        <Tile label="Platform fee (life)" value={`₹${data.summary.lifetime_fees}`} icon={BadgePercent} color="bg-brand/10 text-brand" testid="tile-fees"/>
      </div>

      <section className="bg-surface border border-line rounded-3xl p-5 shadow-soft">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="font-heading text-xl font-bold">Payout account</h2>
          <button onClick={() => setEditingAcc(true)} data-testid="edit-payout" className="text-xs font-bold text-brand hover:underline">{payoutAcc ? "Edit" : "Add"}</button>
        </div>
        {payoutAcc ? (
          <div className="space-y-1 text-sm">
            <div className="text-ink-soft text-xs uppercase tracking-wider font-bold">{payoutAcc.country === "IN" ? "India · Razorpay Route" : "Global · Stripe Connect"}</div>
            {payoutAcc.upi_id && <div>UPI: <b>{payoutAcc.upi_id}</b></div>}
            {payoutAcc.bank_ifsc && <div>Bank: <b>•••• {payoutAcc.bank_account_number?.slice(-4)}</b> · IFSC <b>{payoutAcc.bank_ifsc}</b></div>}
            {payoutAcc.stripe_connected_account_id && <div>Stripe ID: <b>{payoutAcc.stripe_connected_account_id}</b></div>}
            <div className="text-xs text-ink-soft mt-2">Status: <span className={`font-bold ${payoutAcc.verified ? "text-brand-green" : "text-brand"}`}>{payoutAcc.verified ? "Verified" : "Pending activation"}</span></div>
          </div>
        ) : (
          <p className="text-ink-soft text-sm">No payout account on file.</p>
        )}
      </section>

      <section className="bg-surface border border-line rounded-3xl p-5 shadow-soft">
        <h2 className="font-heading text-xl font-bold mb-3">Earnings ledger</h2>
        {data.rows.length === 0 ? (
          <p className="text-ink-soft text-sm">No earnings yet. Complete a booking to start earning.</p>
        ) : (
          <div className="divide-y divide-line">
            {data.rows.map(r => (
              <div key={r.earning_id} className="py-3 flex items-center justify-between gap-3 flex-wrap text-sm">
                <div className="flex-1 min-w-[150px]">
                  <div className="font-bold capitalize">{r.source.replace(/_/g, " ")} · {r.category}</div>
                  <div className="text-xs text-ink-soft">{new Date(r.created_at).toLocaleDateString()} · {r.note || "—"}</div>
                </div>
                <div className="text-right text-sm">
                  <div className="font-heading font-extrabold">₹{r.net_amount}</div>
                  <div className="text-[10px] text-ink-soft">gross ₹{r.gross_amount} – fee ₹{r.platform_fee} ({r.platform_pct}%)</div>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${r.status === "paid" ? "bg-leaf/40 text-ink" : r.status === "in_payout" ? "bg-sky/15 text-sky" : "bg-sun/40 text-ink"}`}>{r.status.replace(/_/g, " ")}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="bg-surface border border-line rounded-3xl p-5 shadow-soft">
        <h2 className="font-heading text-xl font-bold mb-3">Payout history</h2>
        {payouts.length === 0 ? (
          <p className="text-ink-soft text-sm">No payouts yet. Weekly batches run every Monday.</p>
        ) : (
          <div className="divide-y divide-line">
            {payouts.map(p => (
              <div key={p.payout_id} className="py-3 flex items-center justify-between gap-3 flex-wrap text-sm">
                <div>
                  <div className="font-bold">₹{p.amount} <span className="text-ink-soft font-normal text-xs">· {p.earnings_count} bookings · {p.gateway}</span></div>
                  <div className="text-xs text-ink-soft">{new Date(p.created_at).toLocaleDateString()}</div>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${p.status === "paid" ? "bg-leaf/40 text-ink" : "bg-sun/40 text-ink"}`}>{p.status.replace(/_/g, " ")}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {editingAcc && <PayoutAccountModal existing={payoutAcc} onClose={() => { setEditingAcc(false); load(); }}/>}
    </div>
  );
}

function Tile({ label, value, icon: I, color, testid }) {
  return (
    <div data-testid={testid} className="bg-surface border border-line rounded-2xl p-4 shadow-soft">
      <div className={`w-9 h-9 rounded-xl grid place-items-center ${color}`}><I className="w-4 h-4"/></div>
      <div className="text-[11px] uppercase tracking-wider font-bold text-ink-soft mt-2">{label}</div>
      <div className="font-heading text-2xl font-extrabold mt-0.5">{value}</div>
    </div>
  );
}

function PayoutAccountModal({ existing, onClose }) {
  const [country, setCountry] = useState((existing?.country) || "IN");
  const [form, setForm] = useState({
    legal_name: existing?.legal_name || "",
    pan: existing?.pan || "",
    upi_id: existing?.upi_id || "",
    bank_account_number: existing?.bank_account_number || "",
    bank_ifsc: existing?.bank_ifsc || "",
  });
  const [saving, setSaving] = useState(false);
  const [stripeUrl, setStripeUrl] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/partner/payout-account`, { country, ...form });
      onClose();
    } finally { setSaving(false); }
  };

  const startStripe = async () => {
    const { data } = await axios.post(`${API}/partner/stripe-connect/start`);
    if (data.url) window.location.href = data.url;
    else setStripeUrl(data.message || "Stripe Connect not activated yet.");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-xl font-extrabold">Payout details</h3>
          <button onClick={onClose}><X className="w-5 h-5"/></button>
        </div>

        <div className="flex gap-2 mb-4">
          {[{ id: "IN", l: "India · Razorpay" }, { id: "INTL", l: "Global · Stripe" }].map(c => (
            <button key={c.id} onClick={() => setCountry(c.id)} data-testid={`country-${c.id}`} className={`flex-1 rounded-full py-2.5 text-xs font-bold border ${country === c.id ? "bg-ink text-white border-ink" : "bg-surface border-line text-ink-soft"}`}>{c.l}</button>
          ))}
        </div>

        {country === "IN" ? (
          <form onSubmit={submit} className="space-y-3" data-testid="payout-form-IN">
            <F label="Legal name *"><input required value={form.legal_name} onChange={e => setForm({ ...form, legal_name: e.target.value })} className="li" data-testid="po-name"/></F>
            <F label="PAN (optional, but speeds activation)"><input value={form.pan} onChange={e => setForm({ ...form, pan: e.target.value.toUpperCase() })} className="li" placeholder="ABCDE1234F"/></F>
            <div className="text-xs text-ink-soft font-bold mt-3">Provide EITHER UPI OR bank account</div>
            <F label="UPI ID"><input value={form.upi_id} onChange={e => setForm({ ...form, upi_id: e.target.value })} className="li" placeholder="yourname@upi" data-testid="po-upi"/></F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Bank account #"><input value={form.bank_account_number} onChange={e => setForm({ ...form, bank_account_number: e.target.value })} className="li" data-testid="po-acc"/></F>
              <F label="IFSC"><input value={form.bank_ifsc} onChange={e => setForm({ ...form, bank_ifsc: e.target.value.toUpperCase() })} className="li" placeholder="HDFC0000001"/></F>
            </div>
            <div className="bg-sky/10 border border-sky/30 rounded-2xl p-3 text-xs">
              <Building className="inline w-3.5 h-3.5 mr-1"/>Once you save, PetBond will create a <b>Razorpay Route linked account</b>. Activation typically takes 24 hours.
            </div>
            <button disabled={saving} data-testid="po-submit" className="w-full bg-brand text-white rounded-full py-3 font-bold disabled:opacity-60">{saving ? "Saving…" : "Save payout details"}</button>
          </form>
        ) : (
          <div className="space-y-3" data-testid="payout-form-INTL">
            <p className="text-sm text-ink-soft">For partners outside India, we use <b>Stripe Connect Express</b>. Click below to complete Stripe's 3-minute onboarding — they verify your identity & bank info securely.</p>
            <button onClick={startStripe} data-testid="start-stripe-connect" className="w-full bg-[#635BFF] text-white rounded-full py-3 font-bold hover:opacity-90 inline-flex items-center justify-center gap-2"><Banknote className="w-4 h-4"/>Continue with Stripe</button>
            {stripeUrl && <div className="bg-sun/40 border border-sun rounded-2xl p-3 text-xs">{stripeUrl}</div>}
          </div>
        )}

        <style>{`.li{width:100%;border:1px solid #EAE0D5;border-radius:14px;padding:0.55rem 0.9rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;outline:none;}.li:focus{border-color:#FF6B35;}`}</style>
      </div>
    </div>
  );
}

const F = ({ label, children }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-wider font-bold text-ink-soft">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);
